<template>
    <page :busy="busy" :tabs="tabs" :compact-mobile-view="true">
        <grid
            ref="grid"
            :fetch-callback="fetchCallback"
            :column-defs="columnDefs"
            :cacheBlockSize="15"
            :maxBlocksInCache="null"
        >
            <responsive-button
                title="Refresh Table"
                variant="primary"
                :icon="['fas', 'arrows-rotate']"
                @click="refresh"
                >Refresh
            </responsive-button>
            <responsive-button
                title="Add new lockout"
                variant="primary"
                :icon="['fas', 'plus']"
                @click="openCreateLockoutModal()"
                >Add lockout
            </responsive-button>
        </grid>
        <modal
            id="create-lockout"
            title="Create Lockout"
            dialog-class="create-lockout-modal"
            ok-title="Save"
            :scrollable="false"
            :ok-disabled="disabledUpdate"
            @ok.prevent="addLockout"
            @hidden="selectedDestinations = []"
        >
            <div class="source-wrapper">
                <span> When making a reservation for: </span>
                <form-select
                    class="mt-2 mb-4 w-100"
                    :options="sourceItems"
                    v-model="selectedSource"
                    @input="selectedDestinations = []"
                />
                <span>
                    ... lockout the property (or roomtypes) selected below
                </span>
            </div>
            <div class="d-flex flex-column">
                <span class="mb-2 mt-2 text-center"
                    >List Properties / Roomtypes</span
                >
                <lockout-property-select
                    v-model="selectedDestinations"
                    :properties="destinationOptions"
                    placeholder="Properties"
                    class="d-flex w-100"
                />
            </div>
        </modal>
    </page>
</template>

<script>
import API from '../../../api';
import Page from '../../generic/page/page.vue';
import Grid from '../../generic/grid/grid.vue';
import ActionsCellRenderer from './actions-cell-renderer.vue';
import ResponsiveButton from '../../generic/responsive-button/responsive-button.vue';
import LockoutPropertySelect from './lockouts-property-select.vue';
import Modal from '../../generic/modal/modal.vue';
import FormSelect from '../../generic/form-select/form-select.vue';
import FormInput from '../../generic/form-input/form-input.vue';
import { escapeHTML } from '../../../utils/strings';
import { mapGetters, mapActions } from 'vuex';
import { PROPERTY_LOCKOUT_SOURCE_GRID_ENDPOINT } from '../../../endpoints';

export default {
    name: 'LockoutConfigList',
    components: {
        Page,
        Grid,
        ResponsiveButton,
        Modal,
        FormSelect,
        FormInput,
        LockoutPropertySelect,
        actionsCellRenderer: ActionsCellRenderer,
    },
    data() {
        return {
            busy: null,
            tabs: [
                {
                    id: 'lockout-config',
                    title: 'Lockout Config',
                    active: true,
                },
            ],
            selectedSource: null,
            offset: 0,
            limit: 20,
            sort: 'id',
            desc: false,
            search: '',
            selectedDestinations: [],
            columnDefs: [
                {
                    field: 'rental_property.name',
                    headerName: 'Property',
                    sort: 'asc',
                },
                {
                    field: 'property_lockout_destination',
                    headerName: 'Lockout',
                    autoHeight: true,
                    cellRenderer: ({ data }) =>
                       data && data.property_lockout_destination
                            .map((destination) =>
                                escapeHTML(destination.rental_property.name)
                            )
                            .join('<br />'),
                },
                {
                    headerName: '',
                    maxWidth: 130,
                    minWidth: 130,
                    resizable: false,
                    sortable: false,
                    cellStyle: { textAlign: 'center' },
                    cellRenderer: 'actionsCellRenderer',
                    cellRendererParams: {
                        refresh: () => {
                            this.refresh();
                        },
                    },
                },
            ],
        };
    },
    computed: {
        ...mapGetters('propertyLockout', ['destinationItems', 'sourceItems']),
        disabledUpdate() {
            return !this.selectedDestinations.length;
        },
        destinationOptions() {
            return this.destinationItems.filter(
                (item) => item.value !== this.selectedSource
            );
        },
        fetchCallback() {
            return ({ offset, limit, sort, desc, search }) => {
                this.offset = offset;
                this.limit = limit;
                this.sort = sort;
                this.desc = desc;
                this.search = search;
                return API.get(PROPERTY_LOCKOUT_SOURCE_GRID_ENDPOINT(), {
                    params: {
                        offset,
                        limit,
                        sort,
                        desc,
                        search,
                    },
                });
            };
        },
    },
    methods: {
        ...mapActions('propertyLockout', [
            'fetchSourceItems',
            'fetchDestinationItems',
            'createLockout',
        ]),
        refresh() {
            this.$refs.grid.refreshDataSource();
        },
        async openCreateLockoutModal() {
            try {
                await this.fetchSource();
                await this.fetchDestination();
                this.selectedSource = this.sourceItems[0].value;
                this.$bvModal.show('create-lockout');
            } catch (e) {
                this.errorToast({ text: 'Could not load properties' });
            }
        },
        async fetchSource() {
            try {
                await this.fetchSourceItems();
            } catch (e) {
                this.errorToast({text: 'Could not load data.'})
            }
        },
        async fetchDestination() {
            try {
                await this.fetchDestinationItems();
            } catch (e) {
                this.errorToast({text: 'Could not load data.'})
            }
        },
        async addLockout() {
            try {
                this.busy = true;
                await this.createLockout({
                    destination_lockouts: this.selectedDestinations,
                    source_property_room: this.selectedSource,
                });
                this.$bvModal.hide('create-lockout');
                this.refresh();
            } catch (e) {
                this.errorToast({text: 'Could not create property lockout.'})
            } finally {
                this.busy = false;
            }
        },
    },
};
</script>
